/**
 * «Chi segue questo atleta», nella lista Utenti dell'admin.
 *
 * Il bottone sulla riga dice gia' la risposta breve («2 coach», «Nessun
 * coach»): il colore la rinforza, non la sostituisce. Il dettaglio si apre con
 * un clic, non al passaggio del mouse, cosi' funziona anche dal telefono.
 *
 * I dati arrivano da `followed_by` di GET /admin/users, lo stesso cono che
 * «Gestione Coach» legge al contrario (`_coach_cone_links` in
 * routes/admin.py): qui non si ricalcola niente.
 */
import { Building2, UserCog, Users } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

export function athleteCoachCount(user) {
	return Array.isArray(user?.followed_by) ? user.followed_by.length : 0;
}

export function AthleteCoachesPopover({ user, onManage }) {
	const { t } = useLanguage();
	const coaches = Array.isArray(user?.followed_by) ? user.followed_by : [];
	const count = coaches.length;
	// Un account atleta + coach che si scrive i programmi da se' non e'
	// «senza coach»: e' una situazione normale, non un avviso.
	const selfOnly = count === 0 && !!user?.self_coached;
	const label =
		count === 0
			? selfOnly
				? t("adminUserCoachesSelf")
				: t("adminUserCoachesNone")
			: count === 1
				? t("adminUserCoachesOne")
				: t("adminUserCoachesCount").replace("{count}", count);
	const hasShared = coaches.some((c) => c.relation === "shared");

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className={`h-8 rounded-sm gap-1.5 px-2.5 text-xs ${
						selfOnly
							? "border-border bg-background/30 text-muted-foreground"
							: count === 0
								? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
								: "border-acid/50 bg-acid/10 text-acid"
					}`}
					aria-label={`${label} — ${t("adminUserCoachesTitle").replace(
						"{name}",
						user.name || user.email,
					)}`}
					data-testid={`user-coaches-${user.user_id}`}
				>
					<UserCog className="w-4 h-4" aria-hidden="true" />
					{label}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				className="w-[min(22rem,calc(100vw-2rem))] p-0 bg-card border-border"
			>
				<div className="px-4 pt-3 pb-2 border-b border-border">
					<p className="text-sm font-medium">
						{t("adminUserCoachesTitle").replace(
							"{name}",
							user.name || user.email,
						)}
					</p>
					{user?.self_coached && (
						<p className="mt-1 text-[11px] text-muted-foreground">
							{t("adminUserCoachesSelfNote")}
						</p>
					)}
				</div>
				{count === 0 ? (
					<p className="px-4 py-3 text-sm text-muted-foreground">
						{selfOnly
							? t("adminUserCoachesSelfEmpty")
							: t("adminUserCoachesEmpty")}
					</p>
				) : (
					<ul className="max-h-72 overflow-y-auto px-2 py-2 space-y-1">
						{coaches.map((coach) => (
							<li
								key={coach.user_id}
								className="rounded-sm bg-secondary px-2.5 py-2"
							>
								<div className="flex items-baseline justify-between gap-2">
									<p className="text-sm truncate">
										{coach.name || coach.email}
									</p>
									{coach.is_active === false && (
										<Badge variant="destructive" className="text-[10px]">
											{t("adminUserCoachesInactive")}
										</Badge>
									)}
								</div>
								{coach.email && coach.name && (
									<p className="text-[11px] text-muted-foreground truncate">
										{coach.email}
									</p>
								)}
								<div className="mt-1.5 flex flex-wrap gap-1">
									{coach.relation === "direct" && (
										<Badge className="bg-acid/20 text-acid border-0 text-[11px] gap-1">
											<UserCog className="w-3 h-3" aria-hidden="true" />
											{t("adminUserCoachesDirect")}
										</Badge>
									)}
									{(coach.via_teams || []).map((team) => (
										<Badge
											key={`team:${team.team_id}`}
											className="bg-purple-500/20 text-purple-400 border-0 text-[11px] gap-1"
										>
											<Building2 className="w-3 h-3" aria-hidden="true" />
											{t("adminCoachAthletesViaTeam")} {team.name}
										</Badge>
									))}
									{(coach.via_groups || []).map((group) => (
										<Badge
											key={`group:${group.group_id}`}
											variant="outline"
											className="text-[11px] gap-1"
										>
											<Users className="w-3 h-3" aria-hidden="true" />
											{t("adminUserCoachesViaGroup")} {group.name}
										</Badge>
									))}
								</div>
							</li>
						))}
					</ul>
				)}
				<div className="px-4 py-3 border-t border-border space-y-2">
					{hasShared && (
						<p className="text-[11px] text-muted-foreground">
							{t("adminUserCoachesSharedHint")}
						</p>
					)}
					{onManage && (
						<Button
							size="sm"
							variant={count === 0 ? "default" : "outline"}
							className="w-full rounded-sm"
							onClick={() => onManage(user)}
						>
							{count === 0
								? t("adminUserCoachesAssign")
								: t("adminUserCoachesManage")}
						</Button>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
